import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { HourFormat } from '../types.js';

// ─────────────────────────────────────────────────────────────────────────────
// TimeHost
// ─────────────────────────────────────────────────────────────────────────────

export interface TimeHost extends ReactiveControllerHost {
  showTime:    boolean;
  timeOnly:    boolean;
  hourFormat:  HourFormat;
  showSeconds: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// TimeController
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manages time-picker state (hour / minute / second / meridiem).
 * Stub in Phase 1 — wired up in Phase 4.
 */
export class TimeController implements ReactiveController {
  readonly host: TimeHost;

  // Internal 0-23 hour storage
  hour:   number = 0;
  minute: number = 0;
  second: number = 0;

  constructor(host: TimeHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void { /* no-op */ }
  hostDisconnected(): void { /* no-op */ }

  // ── Derived getters ───────────────────────────────────────────────

  get displayHour(): number {
    if (this.host.hourFormat === '12') {
      const h = this.hour % 12;
      return h === 0 ? 12 : h;
    }
    return this.hour;
  }

  get meridiem(): 'AM' | 'PM' {
    return this.hour < 12 ? 'AM' : 'PM';
  }

  // ── Step mutations ────────────────────────────────────────────────

  stepHour(delta: 1 | -1): void {
    this.hour = ((this.hour + delta) + 24) % 24;
    this.host.requestUpdate();
  }

  stepMinute(delta: 1 | -1): void {
    this.minute = ((this.minute + delta) + 60) % 60;
    this.host.requestUpdate();
  }

  stepSecond(delta: 1 | -1): void {
    this.second = ((this.second + delta) + 60) % 60;
    this.host.requestUpdate();
  }

  // ── Direct setters ────────────────────────────────────────────────

  setHour(h: number): void {
    this.hour = Math.max(0, Math.min(23, h));
    this.host.requestUpdate();
  }

  setMinute(m: number): void {
    this.minute = Math.max(0, Math.min(59, m));
    this.host.requestUpdate();
  }

  setSecond(s: number): void {
    this.second = Math.max(0, Math.min(59, s));
    this.host.requestUpdate();
  }

  toggleMeridiem(): void {
    this.hour = (this.hour + 12) % 24;
    this.host.requestUpdate();
  }

  // ── Integration ───────────────────────────────────────────────────

  /** Returns a new Date with the current time fields overlaid. */
  applyToDate(date: Date): Date {
    const result = new Date(date);
    result.setHours(this.hour, this.minute, this.second, 0);
    return result;
  }

  /** Seeds time fields from an existing Date. */
  syncFromDate(date: Date): void {
    this.hour   = date.getHours();
    this.minute = date.getMinutes();
    this.second = date.getSeconds();
  }
}
